import React, { useState } from "react";
import { Panel, FormGroup, ControlLabel } from 'react-bootstrap';
import { useTranslation } from "react-i18next";
import { CustomSelect } from "../../../components/CustomControls/CustomSelect";
import { YesNo } from "../../../components/CustomControls/YesNoControls";
import { clamp, formatSeq } from "../Hooks/protocolReturnRows";
import { QuantityInput } from "../Shared/QuantityInput";

const ReturnResult = {
    Return: 'Return',
    Missed: 'Missed',
};

function resultToDraft(result, damaged) {
    if (result === ReturnResult.Missed) return { releaseTargetState: 'Missed', condition: null };
    return { releaseTargetState: 'Return', condition: damaged ? 'Damaged' : null };
}

export default function ProtocolReturnEntryForm({ comprobantes, remaining, disabled, onAdd }) {
    const { t } = useTranslation();
    const [comprobante, setComprobante] = useState(null);
    const [line, setLine] = useState(null);
    const [returned, setReturned] = useState(0);
    const [result, setResult] = useState(ReturnResult.Return);
    const [damaged, setDamaged] = useState(false);
    const [reDispense, setReDispense] = useState(false);

    const effectiveComprobante = comprobante ?? (comprobantes.length === 1 ? comprobantes[0] : null);
    const itemOptions = (effectiveComprobante?.items ?? [])
        .filter(l => (remaining?.get(l.originMoveItemId) ?? 0) > 0);
    const perUnitTotal = line?.perUnitTotal ?? 0;
    const isReturned = result === ReturnResult.Return;
    const canAdd = !disabled && line != null && (isReturned ? returned > 0 : perUnitTotal > 0);

    const resultOptions = [
        { value: ReturnResult.Return, label: t('inventory.commons.returnResultReturn') },
        { value: ReturnResult.Missed, label: t('inventory.commons.returnResultMissed') },
    ];

    const resetLine = () => {
        setLine(null);
        setReturned(0);
        setResult(ReturnResult.Return);
        setDamaged(false);
        setReDispense(false);
    };

    const onComprobanteChange = (_n, v) => {
        setComprobante(v ?? null);
        resetLine();
    };

    const onLineChange = (_n, v) => {
        setLine(v ?? null);
        setReturned(0);
        setResult(ReturnResult.Return);
        setDamaged(false);
        setReDispense(false);
    };

    const onQuantityChange = (v) => {
        const next = clamp(v, 0, perUnitTotal);
        setReturned(next);
        if (next <= 0) {
            setDamaged(false);
            setReDispense(false);
        }
    };

    const onResultChange = (_n, v) => {
        const next = v?.value ?? ReturnResult.Return;
        setResult(next);
        if (next !== ReturnResult.Return) {
            setDamaged(false);
            setReDispense(false);
        }
    };

    const handleAdd = () => {
        if (!canAdd) return;
        const effectiveReturned = isReturned ? returned : perUnitTotal;
        const effectiveReDispense = isReturned && reDispense;
        onAdd(line.originMoveItemId, { returned: effectiveReturned, reDispense: effectiveReDispense, ...resultToDraft(result, damaged) });
        resetLine();
    };

    return (
        <Panel bsStyle="info" eventKey="return-entry-form">
            <Panel.Heading>
                <Panel.Title toggle>{t('inventory.commons.newReturn')}</Panel.Title>
            </Panel.Heading>
            <Panel.Body collapsible>
                <div className='input-group-container'>
                    <FormGroup style={{ width: "100%" }}>
                        <ControlLabel>{t('inventory.commons.selectComprobante')}</ControlLabel>
                        <CustomSelect
                            name="return-entry-comprobante"
                            isClearable
                            options={comprobantes}
                            value={effectiveComprobante}
                            isDisabled={disabled || comprobantes.length === 1}
                            getOptionLabel={c => `${t('inventory.commons.comprobanteOf', { date: formatSeq(c.sequenceNumber) })} · ${c.summary}`}
                            getOptionValue={c => c.originDispenseMovementId}
                            onChange={onComprobanteChange}
                            placeholder={t('commons.select')}
                        />
                    </FormGroup>
                </div>
                <hr />
                <div className='input-group-container'>
                    <FormGroup style={{ width: "100%" }}>
                        <ControlLabel>{t('inventory.commons.selectComprobanteItem')}</ControlLabel>
                        <CustomSelect
                            name="return-entry-item"
                            isClearable
                            options={itemOptions}
                            value={line}
                            isDisabled={disabled || effectiveComprobante == null}
                            getOptionLabel={l => `${l.presentation} · ${l.release?.lotNumber ?? ''} (${t('inventory.commons.remainingUnits', { n: remaining?.get(l.originMoveItemId) ?? 0 })})`}
                            getOptionValue={l => l.originMoveItemId}
                            onChange={onLineChange}
                            placeholder={t('commons.select')}
                        />
                    </FormGroup>
                </div>
                <hr />
                <div className='input-group-container'>
                    <FormGroup style={{ width: "50%" }}>
                        <ControlLabel>{t('inventory.commons.returnResult')}</ControlLabel>
                        <CustomSelect
                            name="return-entry-result"
                            options={resultOptions}
                            value={resultOptions.find(o => o.value === result) ?? null}
                            isDisabled={disabled || line == null}
                            getOptionLabel={o => o.label}
                            getOptionValue={o => o.value}
                            onChange={onResultChange}
                            placeholder={t('commons.select')}
                        />
                    </FormGroup>
                    {isReturned && (
                        <FormGroup style={{ width: "50%" }}>
                            <ControlLabel htmlFor="return-entry-quantity">{t('inventory.commons.kitQuantity')}</ControlLabel>
                            <div className="protocol-return__kit-progress">
                                <QuantityInput
                                    id="return-entry-quantity"
                                    name="return-entry-quantity"
                                    min={0}
                                    allowEmpty={false}
                                    max={perUnitTotal}
                                    value={returned}
                                    disabled={disabled || line == null}
                                    onChange={onQuantityChange}
                                />
                                <span className="protocol-return__kit-total">{`${returned} / ${perUnitTotal}`}</span>
                            </div>
                        </FormGroup>
                    )}
                </div>
                {isReturned && (
                    <div className='input-group-container'>
                        <FormGroup style={{ width: "50%" }}>
                            <ControlLabel>{t('inventory.stock.condition.damaged')}</ControlLabel>
                            <YesNo
                                name="return-entry-damaged"
                                value={damaged}
                                disabled={disabled || returned <= 0}
                                onChange={(_n, v) => setDamaged(v === true)}
                            />
                        </FormGroup>
                        <FormGroup style={{ width: "50%" }}>
                            <ControlLabel>{t('inventory.commons.reDispense')}</ControlLabel>
                            <YesNo
                                name="return-entry-redispense"
                                value={reDispense}
                                disabled={disabled || returned <= 0}
                                onChange={(_n, v) => setReDispense(v === true)}
                            />
                        </FormGroup>
                    </div>
                )}
                <div style={{ display: "flex", flexDirection: 'column', justifyContent: 'end' }}>
                    <div style={{ alignSelf: 'self-end', marginTop: '1rem' }}>
                        <button type="button" className='btn btn-success' disabled={!canAdd} onClick={handleAdd}>
                            {t('buttons.add')}
                        </button>
                    </div>
                </div>
            </Panel.Body>
        </Panel>
    );
}
